import type { ApplicantEntry } from '@/types';
import { ref } from 'yup';
import { yupAddress, yupObject, yupString } from '../../yup';
import { ApplicantType, Gender } from '@/helpers/const';
import dayjs from '@/helpers/dayjs';

/**
 * ERHL-specific applicant validation schema.
 * Extends the standard applicant rules with an age range check (50–80).
 */
export const erhlApplicantSchema = (
  primary?: ApplicantEntry,
  allApplicants: ApplicantEntry[] = [],
  currentId?: string,
) => {
  return yupObject({
    type: yupString().oneOf(Object.values(ApplicantType)).required(),
    firstName: yupString()
      .required()
      .test('len', 'Max characters input is 128', (val) => !val || val.length <= 128)
      .label('First name'),
    middleName: yupString()
      .test('len', 'Max characters input is 128', (val) => (val !== undefined ? val.length <= 128 : true))
      .label('Middle name'),
    lastName: yupString()
      .required()
      .test('len', 'Max characters input is 128', (val) => !val || val.length <= 128)
      .label('Last name'),
    preferredName: yupString()
      .required()
      .test('len', 'Max characters input is 128', (val) => !val || val.length <= 128)
      .label('Preferred name'),
    gender: yupString().oneOf(Object.values(Gender)).required().label('Gender'),
    genderOther: yupString()
      .when('gender', {
        is: Gender.OTHER,
        then: (schema) => schema.required(),
      })
      .label('Gender identity'),
    birthdate: yupString()
      .test(
        'is-valid-birthdate',
        'Birthday cannot be invalid or be a future date',
        (value) => dayjs().format('YYYY-MM-DD') >= (value ?? ''),
      )
      .required()
      .label('Birthday'),
    address: yupAddress().required().label('Property address'),
    mobile: yupString()
      .matches(/^(\+\d{11,15}|04\d{8})$/, '${path} must be valid')
      .test('unique-mobile', '', function (value) {
        if (!value) return true;
        const trimmedValue = value.trim();
        const id = currentId ?? this.parent.id;

        if (primary?.id === id) return true;

        if (primary?.mobile?.trim() === trimmedValue) {
          return this.createError({ message: 'Cannot use the same mobile number as the primary applicant' });
        }

        const duplicateSecondary = allApplicants.find((a) => a.id !== id && a.mobile?.trim() === trimmedValue);

        if (duplicateSecondary) {
          return this.createError({ message: 'Mobile number is already used by another secondary applicant' });
        }

        return true;
      })
      .required()
      .label('Mobile number'),
    email: yupString()
      .email()
      .test('unique-email', '', function (value) {
        if (!value) return true;
        const normalizedValue = value.trim().toLowerCase();
        const id = currentId ?? this.parent.id;

        if (primary?.id === id) return true;

        if (primary?.email?.trim()?.toLowerCase() === normalizedValue) {
          return this.createError({ message: 'Cannot use the same email as the primary applicant' });
        }

        const duplicateSecondary = allApplicants.find(
          (a) => a.id !== id && a.email?.trim()?.toLowerCase() === normalizedValue,
        );

        if (duplicateSecondary) {
          return this.createError({ message: 'Email is already used by another secondary applicant' });
        }

        return true;
      })
      .required()
      .label('Email address'),
    confirmEmail: yupString()
      .oneOf([ref('email'), ''], 'Email must match')
      .required()
      .label('Email confirmation'),
    relationshipStatus: yupString().required().label('Relationship status'),
  });
};
